import { SkillCategory } from '../../types/skillProgress';

export const pickleballSkillCategories: SkillCategory[] = [
  {
    id: 'serving',
    name: 'Serving',
    description: 'Master the art of serving in pickleball',
    skills: [
      {
        id: 'basic-serve',
        name: 'Basic Serve',
        description: 'Execute a legal underhand serve that clears the net',
        difficulty: 'beginner',
        tips: [
          'Keep your paddle below your wrist',
          'Contact the ball below your waist',
          'Follow through toward your target',
          'Keep your feet behind the baseline'
        ]
      },
      {
        id: 'serve-placement',
        name: 'Serve Placement',
        description: 'Place serves to different areas of the court',
        difficulty: 'beginner',
        prerequisites: ['basic-serve'],
        tips: [
          'Aim for the corners to create angles',
          'Mix up deep and short serves',
          'Target your opponent\'s weaker side',
          'Use the court geometry to your advantage'
        ]
      },
      {
        id: 'power-serve',
        name: 'Power Serve',
        description: 'Add pace and spin to your serve',
        difficulty: 'intermediate',
        prerequisites: ['basic-serve', 'serve-placement'],
        tips: [
          'Generate power from your legs and core',
          'Use a continental grip for better control',
          'Add topspin for more consistent power',
          'Practice timing and rhythm'
        ]
      },
      {
        id: 'spin-serve',
        name: 'Spin Serve',
        description: 'Master different types of spin serves',
        difficulty: 'advanced',
        prerequisites: ['power-serve'],
        tips: [
          'Practice topspin, backspin, and sidespin',
          'Use spin to control bounce height',
          'Vary spin to keep opponents guessing',
          'Focus on consistency over power'
        ]
      }
    ]
  },
  {
    id: 'groundstrokes',
    name: 'Groundstrokes',
    description: 'Develop consistent groundstroke technique',
    skills: [
      {
        id: 'forehand-basic',
        name: 'Basic Forehand',
        description: 'Execute a proper forehand groundstroke',
        difficulty: 'beginner',
        tips: [
          'Use a continental grip',
          'Keep your paddle face square to the ball',
          'Follow through across your body',
          'Stay balanced throughout the stroke'
        ]
      },
      {
        id: 'backhand-basic',
        name: 'Basic Backhand',
        description: 'Execute a proper backhand groundstroke',
        difficulty: 'beginner',
        tips: [
          'Use a continental or eastern backhand grip',
          'Keep your non-dominant hand on the paddle',
          'Rotate your shoulders for power',
          'Follow through toward your target'
        ]
      },
      {
        id: 'groundstroke-placement',
        name: 'Groundstroke Placement',
        description: 'Place groundstrokes to different areas of the court',
        difficulty: 'intermediate',
        prerequisites: ['forehand-basic', 'backhand-basic'],
        tips: [
          'Aim for the corners and sidelines',
          'Use cross-court shots to create angles',
          'Hit behind your opponent when they\'re moving',
          'Practice depth control'
        ]
      },
      {
        id: 'power-groundstroke',
        name: 'Power Groundstroke',
        description: 'Add pace and power to groundstrokes',
        difficulty: 'intermediate',
        prerequisites: ['groundstroke-placement'],
        tips: [
          'Generate power from your legs and core',
          'Use proper weight transfer',
          'Keep your head still during contact',
          'Follow through completely'
        ]
      }
    ]
  },
  {
    id: 'net-play',
    name: 'Net Play',
    description: 'Master the kitchen and net area',
    skills: [
      {
        id: 'dink-basic',
        name: 'Basic Dink',
        description: 'Execute a soft, controlled shot over the net',
        difficulty: 'beginner',
        tips: [
          'Keep your paddle face open',
          'Use your wrist, not your arm',
          'Aim for the kitchen area',
          'Stay balanced and ready to move'
        ]
      },
      {
        id: 'dink-placement',
        name: 'Dink Placement',
        description: 'Place dinks to different areas of the kitchen',
        difficulty: 'intermediate',
        prerequisites: ['dink-basic'],
        tips: [
          'Aim for the corners of the kitchen',
          'Use cross-court dinks to create angles',
          'Mix up placement to keep opponents guessing',
          'Focus on consistency over power'
        ]
      },
      {
        id: 'volley-basic',
        name: 'Basic Volley',
        description: 'Execute a proper volley at the net',
        difficulty: 'intermediate',
        tips: [
          'Keep your paddle up and ready',
          'Use a short, compact swing',
          'Step into the ball',
          'Keep your eyes on the ball'
        ]
      },
      {
        id: 'overhead-smash',
        name: 'Overhead Smash',
        description: 'Execute a powerful overhead shot',
        difficulty: 'advanced',
        prerequisites: ['volley-basic'],
        tips: [
          'Get into position quickly',
          'Use a continental grip',
          'Hit the ball at the highest point',
          'Follow through toward your target'
        ]
      }
    ]
  },
  {
    id: 'strategy',
    name: 'Strategy & Tactics',
    description: 'Develop strategic thinking and court awareness',
    skills: [
      {
        id: 'court-positioning',
        name: 'Court Positioning',
        description: 'Understand proper court positioning',
        difficulty: 'beginner',
        tips: [
          'Stay in the center of your side',
          'Move as a team in doubles',
          'Cover your partner\'s weaknesses',
          'Anticipate your opponent\'s shots'
        ]
      },
      {
        id: 'shot-selection',
        name: 'Shot Selection',
        description: 'Choose the right shot for each situation',
        difficulty: 'intermediate',
        prerequisites: ['court-positioning'],
        tips: [
          'Consider your opponent\'s position',
          'Use the right shot for the right situation',
          'Don\'t try to win every point with power',
          'Be patient and wait for opportunities'
        ]
      },
      {
        id: 'third-shot-drop',
        name: 'Third Shot Drop',
        description: 'Master the crucial third shot',
        difficulty: 'intermediate',
        prerequisites: ['shot-selection'],
        tips: [
          'Hit a soft, high-arcing shot',
          'Aim for the kitchen line',
          'Move forward after the shot',
          'Use this shot to get to the net'
        ]
      },
      {
        id: 'advanced-strategy',
        name: 'Advanced Strategy',
        description: 'Develop sophisticated strategic thinking',
        difficulty: 'advanced',
        prerequisites: ['third-shot-drop'],
        tips: [
          'Study your opponent\'s patterns',
          'Use deception and variety',
          'Control the pace of the game',
          'Exploit your opponent\'s weaknesses'
        ]
      }
    ]
  },
  {
    id: 'mental-game',
    name: 'Mental Game',
    description: 'Develop mental toughness and focus',
    skills: [
      {
        id: 'focus-concentration',
        name: 'Focus & Concentration',
        description: 'Maintain focus throughout the match',
        difficulty: 'beginner',
        tips: [
          'Stay in the present moment',
          'Don\'t dwell on mistakes',
          'Focus on the next point',
          'Use breathing techniques to stay calm'
        ]
      },
      {
        id: 'pressure-handling',
        name: 'Pressure Handling',
        description: 'Perform well under pressure',
        difficulty: 'intermediate',
        prerequisites: ['focus-concentration'],
        tips: [
          'Practice pressure situations',
          'Develop pre-shot routines',
          'Stay positive and confident',
          'Use visualization techniques'
        ]
      },
      {
        id: 'match-strategy',
        name: 'Match Strategy',
        description: 'Develop and execute match strategies',
        difficulty: 'advanced',
        prerequisites: ['pressure-handling'],
        tips: [
          'Adapt your strategy during the match',
          'Exploit your opponent\'s weaknesses',
          'Stay patient and disciplined',
          'Make adjustments as needed'
        ]
      }
    ]
  }
];
